
import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalVariant,
  Button,
  ButtonVariant,
  Alert,
} from '@patternfly/react-core';
import { useDispatch, useSelector } from 'react-redux';

import { Spot } from 'domain/Spot';
import { Employee } from 'domain/Employee';
import { employeeSelectors } from 'store/employee';
import { spotOperations } from 'store/spot';
import { timeBucketSelectors } from 'store/rotation';
import MultiTypeaheadSelectInput from 'ui/components/MultiTypeaheadSelectInput';

interface Props {
  spot: Spot;
  isOpen: boolean;
  onClose: () => void;
}

const SpotBlockedEmployeesModal: React.FC<Props> = ({
  spot,
  isOpen,
  onClose,
}) => {
  const dispatch = useDispatch();

  const employeeList = useSelector(employeeSelectors.getEmployeeList);

  const [selectedEmployees, setSelectedEmployees] = useState<Employee[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const timeBucketList = useSelector(
    timeBucketSelectors.getTimeBucketList
  );

  const rotationEmployees = React.useMemo(() => {
    const employees: Employee[] = [];

    // Rotaciones con periodicidad superior a 8 días
    if (spot.rotationEmployee) {
      employees.push(spot.rotationEmployee);
    }

    // Rotaciones configuradas en la interfaz de Rotación
    timeBucketList
      .filter((tb) => tb.spot.id === spot.id)
      .forEach((tb) => {
        tb.seatList.forEach((seat) => {
          if (seat !== null && seat.employee !== null) {
            const employee = seat.employee as Employee;

            if (!employees.some((e) => e.id === employee.id)) {
              employees.push(employee);
            }
          }
        });
      });

    return employees;
  }, [spot, timeBucketList]);

  const conflictingEmployees = rotationEmployees.filter(
    (rotationEmployee) =>
      rotationEmployee != null &&
      selectedEmployees.some(
        (employee) =>
          employee != null &&
          employee.id === rotationEmployee.id
      )
  );

  const hasRotationConflict = conflictingEmployees.length > 0;

  useEffect(() => {
    if (!isOpen) return;

    const blockedEmployees = (spot.blockedEmployeeSet || [])
      .filter((employee): employee is Employee => employee != null)
      .map((employee) =>
        employeeList.find((e) => e.id === employee.id) || employee
      );

    setSelectedEmployees(blockedEmployees);
    setError(null);
  }, [isOpen, spot, employeeList]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      const employeeIds = selectedEmployees.map((employee) => {
        if (employee.id == null) {
          throw new Error('Hay un empleado sin identificador.');
        }
        return employee.id;
      });

      await dispatch(
        spotOperations.updateBlockedEmployees({
          spot,
          employeeIds,
        })
      );

      await dispatch(spotOperations.refreshSpotList());

      onClose();
    } catch (err) {
      console.error('Error al guardar empleados bloqueados:', err);
      setError('No fue posible guardar los empleados bloqueados.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      variant={ModalVariant.medium}
      title="Empleados bloqueados"
      isOpen={isOpen}
      onClose={onClose}
      actions={[
        <Button
          key="save"
          variant={ButtonVariant.primary}
          onClick={handleSave}
          isDisabled={isSaving}
          isLoading={isSaving}
        >
          Guardar cambios
        </Button>,
        <Button
          key="cancel"
          variant={ButtonVariant.link}
          onClick={onClose}
          isDisabled={isSaving}
        >
          Cancelar
        </Button>,
      ]}
    >
      <p style={{ marginBottom: '15px' }}>
        <strong>Puesto de trabajo:</strong> {spot.name}
      </p>

      <p style={{ marginBottom: '15px' }}>
        Selecciona los empleados que no pueden ser asignados
        a turnos en este puesto de trabajo.
      </p>

      {error && (
        <Alert
          variant="danger"
          title={error}
          isInline
          style={{ marginBottom: '15px' }}
        />
      )}

      <MultiTypeaheadSelectInput
        value={selectedEmployees.filter(
          (employee): employee is Employee => employee != null
        )}
        options={employeeList.filter(
          (employee): employee is Employee => employee != null
        )}
        optionToStringMap={(employee) => employee?.name ?? ''}
        onChange={setSelectedEmployees}
        emptyText="Seleccionar empleados bloqueados"
      />
      {hasRotationConflict && (
      <Alert
        variant="warning"
        title="Advertencia: conflicto con la rotación"
        isInline
        style={{ marginTop: '15px' }}
      >
        <strong>
          {conflictingEmployees.map((employee) => employee.name).join(', ')}
        </strong>
        {' '}
        {conflictingEmployees.length === 1
          ? 'está asignado como empleado fijo'
          : 'están asignados como empleados fijos'}
        {' '}
        en la rotación de este puesto. Al bloquear
        {conflictingEmployees.length === 1 ? 'lo' : 'los'},
        {' '}
        no podrá
        {conflictingEmployees.length === 1 ? '' : 'n'}
        {' '}
        ser asignado
        {conflictingEmployees.length === 1 ? '' : 's'}
        {' '}
        en las futuras programaciones.
        <br />
        <strong>
          Se recomienda actualizar la rotación y asignar
          {conflictingEmployees.length === 1
            ? ' otro empleado en su lugar.'
            : ' otros empleados en su lugar.'}
        </strong>
      </Alert>
    )}

      <p style={{ marginTop: '15px', color: '#6a6e73' }}>
        Total de empleados bloqueados: {selectedEmployees.length}
      </p>
    </Modal>
  );
};

export default SpotBlockedEmployeesModal;
